import toastUtils from "./toast";
import logger from "./logger";


// Return a user-facing message based on API response or error
const formatOp = (operation = "operation") => (operation || "operation").toLowerCase();

// Normalize details into an array of readable strings and format for display
const translateValidationError = d => {
  if (!d || typeof d !== "object" || !d.attr) return null;

  const map = {
    minDigitCount: `must contain at least ${d.expected} digit(s)`,
    minUppercaseCount: `must contain at least ${d.expected} uppercase letter(s)`,
    minLowercaseCount: `must contain at least ${d.expected} lowercase letter(s)`,
    minSymbolCount: `must contain at least ${d.expected} special character(s)`,
    minLength: `must be at least ${d.expected} characters long`,
    maxLength: `must be no more than ${d.expected} characters long`,
  };

  const message = map[d.attr];
  if (!message) return null;

  // Capitalize for display as a standalone requirement
  return message.charAt(0).toUpperCase() + message.slice(1);
};

const formatDetails = details => {
  if (!details) return "";

  const toLines = () => {
    if (Array.isArray(details)) {
      return details
        .map(d => {
          if (typeof d === "string") return d;
          if (d && typeof d === "object") {
            const translated = translateValidationError(d);
            if (translated) return translated;
            return d.message || d.detail || JSON.stringify(d);
          }
          return d !== undefined && d !== null ? String(d) : "";
        })
        .filter(Boolean);
    }

    if (typeof details === "string") return [details];

    if (details && typeof details === "object") {
      const translated = translateValidationError(details);
      if (translated) return [translated];
      const text = details.message || details.detail || JSON.stringify(details);
      return text ? [text] : [];
    }

    return [];
  };

  const lines = toLines();
  if (!lines.length) return "";

  // Bullet list for readability in toasts; single item stays simple
  if (lines.length === 1) return lines[0];
  return lines.map(line => `• ${line}`).join("\n");
};

// Combine base message with formatted details
const combineMessageAndDetails = (base, detailsText) => {
  const genericValidationMessages = ["request validation failed", "validation failed", "invalid request"];

  if (!detailsText) {
    if (base && genericValidationMessages.includes(base.toLowerCase())) {
      return "Request validation failed. Please check that your inputs (such as phone number and email) are formatted correctly.";
    }
    return base;
  }

  if (base && genericValidationMessages.includes(base.toLowerCase())) {
    return detailsText;
  }

  // Use newline to keep long detail lists readable in toasts
  return `${base}\nDetails:\n${detailsText}`;
};

// Capitalize the first letter of a sentence
const capitalizeFirst = text => {
  if (!text || typeof text !== "string") return text;
  return text.charAt(0).toUpperCase() + text.slice(1);
};

const toPastTense = (operation = "operation") => {
  const op = formatOp(operation).trim();
  if (!op) return "completed operation";

  const phraseMap = {
    "payment setup": "saved payment setup",
    "create payment setup": "created payment setup",
    "update payment setup": "updated payment setup",
    "delete payment setup": "deleted payment setup",
    "setup payment": "set up payment",
    "set up payment": "set up payment",
    "payment method": "saved payment method",
    "load payment data": "loaded payment data",
    "load payment methods": "loaded payment methods",
    "delete payment method": "deleted payment method",
  };

  if (phraseMap[op]) return phraseMap[op];

  const parts = op.split(" ");
  if (parts.length === 0) return op;
  const verb = parts[0];
  const rest = parts.slice(1).join(" ");
  const map = {
    create: "created",
    update: "updated",
    delete: "deleted",
    remove: "removed",
    cancel: "cancelled",
    fetch: "fetched",
    load: "loaded",
    save: "saved",
    add: "added",
    link: "linked",
    unlink: "unlinked",
    invite: "invited",
    resend: "resent",
    submit: "submitted",
    request: "requested",
    setup: "set up",
    set: "set",
    configure: "configured",
    complete: "completed",
    enable: "enabled",
    disable: "disabled",
    verify: "verified",
    send: "sent",
    reset: "reset",
    change: "changed",
    process: "processed",
  };

  if (map[verb]) {
    const past = map[verb];
    return rest ? `${past} ${rest}` : past;
  }

  const lastWord = parts[parts.length - 1];
  if (lastWord === "setup") {
    return `${parts.slice(0, -1).join(" ")} set up`.trim();
  }

  return `completed ${op}`;
};

export const getApiMessage = (errorOrResponse, operation = "operation") => {
  // Debug logging for 204 responses
  if (errorOrResponse?.status === 204) {
    logger.log("DEBUG: 204 response detected:", errorOrResponse);
  }

  // Success response path (has status property)
  if (errorOrResponse && typeof errorOrResponse.status === "number") {
    if (errorOrResponse.status >= 200 && errorOrResponse.status < 300) {
      // For 204 No Content, default to "Success" if no message provided
      const defaultMsg = errorOrResponse.status === 204 ? "Success" : `successfully ${toPastTense(operation)}`;
      const message = errorOrResponse?.data?.message || errorOrResponse?.message || defaultMsg;
      return capitalizeFirst(message);
    }
  }

  // Handle successful status codes within a response object (e.g., Axios error that isn't actually an error)
  if (errorOrResponse?.response && typeof errorOrResponse.response.status === "number") {
    const respStatus = errorOrResponse.response.status;
    if (respStatus >= 200 && respStatus < 300) {
      const defaultMsg = respStatus === 204 ? "Success" : `successfully ${toPastTense(operation)}`;
      const message = errorOrResponse.response.data?.message || errorOrResponse.response?.message || defaultMsg;
      return capitalizeFirst(message);
    }
  }

  // Check if this is a successful API response without status (like updateSelfUser returns response.data)
  if (errorOrResponse && !errorOrResponse.response && !errorOrResponse.status) {
    // This is likely a successful response from methods that return response.data directly
    const message = errorOrResponse?.message || `successfully ${toPastTense(operation)}`;
    return capitalizeFirst(message);
  }

  // Error response path
  const status = errorOrResponse?.response?.status || errorOrResponse?.status;
  let backendMessage =
    errorOrResponse?.response?.data?.message || errorOrResponse?.data?.message || errorOrResponse?.message;
  const backendDetails = errorOrResponse?.response?.data?.details || errorOrResponse?.data?.details;

  // Check if message is a JSON string that needs parsing (422 error with nested JSON)
  // Example: message: "{\"error\":\"NO_SUITABLE_SUBSCRIPTION\",\"message\":\"...\",\"details\":[...]}"
  if (backendMessage && typeof backendMessage === "string") {
    try {
      const parsed = JSON.parse(backendMessage);
      if (parsed && typeof parsed === "object" && parsed.message) {
        backendMessage = parsed.message;
        // Prefer details from parsed JSON if provided
        if (!backendDetails && parsed.details) {
          errorOrResponse.response.data.details = parsed.details;
        }
      }
    } catch (e) {
      // Not valid JSON, use the string as-is
    }
  }

  // Check if message is an object (NO_SUITABLE_SUBSCRIPTION error structure)
  // Structure: { error: "...", message: "...", details: [...] }
  // Extract the nested message property if it exists
  if (
    backendMessage &&
    typeof backendMessage === "object" &&
    !Array.isArray(backendMessage) &&
    backendMessage.message
  ) {
    backendMessage = backendMessage.message;
  }

  const detailsText = formatDetails(backendDetails);

  if (typeof status === "number") {
    if (status >= 500) {
      return "A server error occurred. Please try again later.";
    }
    if (status >= 400 && status < 500) {
      const base = backendMessage || `failed to ${formatOp(operation)}`;
      const message = combineMessageAndDetails(base, detailsText);
      return capitalizeFirst(message);
    }
  }

  const baseMessage = backendMessage || `failed to ${formatOp(operation)}`;
  const message = combineMessageAndDetails(baseMessage, detailsText);
  return capitalizeFirst(message);
};

// Show a toast based on API response or error using standardized rules
export const showApiMessage = (errorOrResponse, operation = "operation") => {
  // Debug logging for all responses
  logger.log("DEBUG: showApiMessage called with:", { errorOrResponse, operation });

  // Filter out NO_SUITABLE_SUBSCRIPTION toasts as requested by user
  const checkIgnored = err => {
    const data = err?.response?.data || err?.data;
    if (!data) return false;

    // Check for error code in data or nested message object
    const errorCode = data.error || (typeof data.message === "object" ? data.message.error : null);
    if (errorCode === "NO_SUITABLE_SUBSCRIPTION") return true;

    // Also check for the specific message text
    const messageText = typeof data.message === "string" ? data.message : data.message?.message;
    if (messageText && messageText.includes("No suitable subscription found")) return true;

    return false;
  };

  if (checkIgnored(errorOrResponse)) {
    logger.log("DEBUG: Suppressing subscription-related toast as requested");
    return;
  }

  // Check for CORS/Network errors first (no response from server)
  if (errorOrResponse && errorOrResponse.message && !errorOrResponse.response && !errorOrResponse.status) {
    // Check if it's a network error or CORS error
    if (
      errorOrResponse.message.toLowerCase().includes("network error") ||
      errorOrResponse.message.toLowerCase().includes("cors") ||
      errorOrResponse.code === "ERR_NETWORK"
    ) {
      toastUtils.error("Cross Origin Policy Error - Unable to connect to server");
      return;
    }
  }

  // Check if this is a success response (has status property)
  if (errorOrResponse && typeof errorOrResponse.status === "number") {
    // Handle all 2xx status codes as success
    if (errorOrResponse.status >= 200 && errorOrResponse.status < 300) {
      // For 204 No Content, default to "Success" if no message provided
      const defaultMsg = errorOrResponse.status === 204 ? "Success" : `successfully ${toPastTense(operation)}`;
      // Prefer message property if explicitly provided by interceptor, then fallback to data.message
      const message = errorOrResponse.message || errorOrResponse?.data?.message || defaultMsg;
      toastUtils.success(capitalizeFirst(message));
      return;
    }
    // If it has a status but it's not success, it's an error
    if (errorOrResponse.status >= 400) {
      let message = errorOrResponse?.data?.message;

      // Check if message is a JSON string that needs parsing (422 error with nested JSON)
      if (message && typeof message === "string") {
        try {
          const parsed = JSON.parse(message);
          if (parsed && typeof parsed === "object" && parsed.message) {
            message = parsed.message;
          }
        } catch (e) {
          // Not valid JSON, use the string as-is
        }
      }

      // Check if message is an object with nested message property (NO_SUITABLE_SUBSCRIPTION structure)
      if (message && typeof message === "object" && !Array.isArray(message) && message.message) {
        message = message.message;
      }

      message = message || getApiMessage(errorOrResponse, operation);
      toastUtils.error(capitalizeFirst(message));
      return;
    }
  }

  // Check if errorOrResponse is a plain string
  if (typeof errorOrResponse === "string") {
    if (operation === "error") {
      toastUtils.error(capitalizeFirst(errorOrResponse));
    } else if (operation === "success") {
      toastUtils.success(capitalizeFirst(errorOrResponse));
    } else if (operation === "warning") {
      toastUtils.warning(capitalizeFirst(errorOrResponse));
    } else {
      toastUtils.info(capitalizeFirst(errorOrResponse));
    }
    return;
  }

  // Check if errorOrResponse is a standard Error object without Axios response
  if (errorOrResponse instanceof Error && !errorOrResponse.response) {
    toastUtils.error(capitalizeFirst(errorOrResponse.message || `Failed to ${formatOp(operation)}`));
    return;
  }

  // Check if this is a successful API response without a numeric status (like updateSelfUser returns response.data)
  // or a backend response with a string status like { status: "CHECKED_IN", message: "..." }
  if (errorOrResponse && !errorOrResponse.response && typeof errorOrResponse.status !== "number") {
    const isErrorOp = operation === "validation" || operation === "error";
    const defaultMsg = isErrorOp ? `Failed to ${formatOp(operation)}` : `successfully ${toPastTense(operation)}`;
    const message = errorOrResponse?.message || defaultMsg;
    if (isErrorOp) {
      toastUtils.error(capitalizeFirst(message));
    } else {
      toastUtils.success(capitalizeFirst(message));
    }
    return;
  }

  // Error handling for axios error responses (errorOrResponse.response exists)
  const status = errorOrResponse?.response?.status;
  let backendMessage = errorOrResponse?.response?.data?.message;
  const backendDetails = errorOrResponse?.response?.data?.details;

  // Check if message is a JSON string that needs parsing (422 error with nested JSON)
  // Example: message: "{\"error\":\"NO_SUITABLE_SUBSCRIPTION\",\"message\":\"...\",\"details\":[...]}"
  if (backendMessage && typeof backendMessage === "string") {
    try {
      const parsed = JSON.parse(backendMessage);
      if (parsed && typeof parsed === "object" && parsed.message) {
        backendMessage = parsed.message;
        if (!backendDetails && parsed.details) {
          errorOrResponse.response.data.details = parsed.details;
        }
        // Update the error response data so getApiMessage can use it
        if (errorOrResponse?.response?.data) {
          errorOrResponse.response.data.message = backendMessage;
        }
      }
    } catch (e) {
      // Not valid JSON, use the string as-is
    }
  }

  // Check if message is an object (NO_SUITABLE_SUBSCRIPTION error structure)
  // Structure: { error: "...", message: "...", details: [...] }
  // Extract the nested message property if it exists
  if (
    backendMessage &&
    typeof backendMessage === "object" &&
    !Array.isArray(backendMessage) &&
    backendMessage.message
  ) {
    backendMessage = backendMessage.message;
    // Update the error response data so getApiMessage can use it
    if (errorOrResponse?.response?.data) {
      errorOrResponse.response.data.message = backendMessage;
    }
  }

  const message = getApiMessage(errorOrResponse, operation);

  // Handle successful status codes that might have been passed as an "error" object
  if (typeof status === "number" && status >= 200 && status < 300) {
    toastUtils.success(message);
    return;
  }

  if (typeof status === "number" && status >= 500) {
    toastUtils.error(message);
    return;
  }

  if (typeof status === "number" && status >= 400) {
    toastUtils.error(message);
    return;
  }

  // Default to error for any other case
  toastUtils.error(message);
};

const messageHandler = {
  getApiMessage,
  showApiMessage,
};

export default messageHandler;
